import React, { useMemo, useEffect } from 'react'
import { Control, UseFormSetValue, useWatch, Controller } from 'react-hook-form'
import { Stack } from '@sureco/design-system'
import Switch from 'antd/es/switch'
import RHFSelect from './form/RHFSelect'
import RHFInput from './form/RHFInput'
import RHFCurrencyInput from './form/RHFCurrencyInput'
import PartialFieldChipSelector from './PartialFieldChipSelector'
import TemplatePicker from './TemplatePicker'
import { PlanBenefitOverrideRuleTemplateKey } from '../enums/plan-benefit-override-rule.enum'
import { useGetPlanBenefits } from '../hooks/useGetPlanBenefits'
import type { PlanBenefitOverrideFormValues } from './planBenefitOverrideForm.schema'
import styles from './PlanBenefitOverrideForm.module.scss'

type Props = {
  control: Control<PlanBenefitOverrideFormValues>
  setValue: UseFormSetValue<PlanBenefitOverrideFormValues>
  isEditMode?: boolean
  isReadOnly?: boolean
  initialSelectedFields?: string[]
  onPartialSelectionChange?: (selected: Set<string>) => void
}

export default function ChangeValueCard({
  control,
  setValue,
  isEditMode = false,
  isReadOnly = false,
  initialSelectedFields,
  onPartialSelectionChange,
}: Props) {
  const template = useWatch({ control, name: 'template' })
  const benefitName = useWatch({ control, name: 'benefitName' })
  const {
    benefits,
    benefitsByName,
    isLoading: isBenefitsLoading,
    error: benefitsError,
  } = useGetPlanBenefits()

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return [currentYear, currentYear + 1].map((y) => ({
      value: y,
      label: String(y),
    }))
  }, [])

  const benefitOptions = useMemo(
    () => benefits.map((b) => ({ value: b.name, label: b.displayName })),
    [benefits]
  )

  const isImmutable = isEditMode || isReadOnly

  useEffect(() => {
    if (template !== PlanBenefitOverrideRuleTemplateKey.UPSERT_RULE) return
    if (!benefitName) return
    const benefit = benefitsByName[benefitName as keyof typeof benefitsByName]
    if (benefit?.sortOrder != null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue('params.sort' as any, benefit.sortOrder)
    }
  }, [benefitName, template, benefitsByName, setValue])

  return (
    <div className={styles.stepList}>
      {/* ── Step 1: Target ── */}
      <div className={styles.step}>
        <div className={styles.stepLeft}>
          <div className={styles.stepBadge}>1</div>
          <div className={styles.stepRail} />
        </div>
        <div className={styles.stepContent}>
          <p className={styles.stepTitle}>Target</p>
          <div className={styles.detailsInlineRow}>
            <RHFSelect
              name="year"
              control={control}
              label="Plan Year"
              required
              options={yearOptions}
              disabled={isImmutable}
            />
            <RHFInput
              name="hiosPlanId"
              control={control}
              label="HIOS Plan ID"
              required
              placeholder="12345AB0000000"
              disabled={isImmutable}
            />
          </div>

          <div className={`${styles.halfWidth} ${styles.upsertSortInput}`}>
            <RHFSelect
              name="benefitName"
              control={control}
              label="Benefit Type"
              required
              options={benefitOptions}
              placeholder={
                benefitsError
                  ? 'Failed to load benefits'
                  : isBenefitsLoading
                    ? 'Loading benefits…'
                    : 'Select a benefit'
              }
              disabled={
                isBenefitsLoading ||
                Boolean(benefitsError) ||
                isEditMode ||
                isReadOnly
              }
            />
          </div>
        </div>
      </div>

      {/* ── Step 2: Override type ── */}
      <div className={styles.step}>
        <div className={styles.stepLeft}>
          <div className={styles.stepBadge}>2</div>
        </div>
        <div className={`${styles.stepContent} ${styles.stepContentLast}`}>
          <p className={styles.stepTitle}>Override type</p>
          <TemplatePicker
            value={template}
            onChange={(key) => setValue('template', key)}
            disabled={isImmutable}
          />

          {template ===
            PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE && (
            <PartialFieldChipSelector
              control={control}
              setValue={setValue}
              isReadOnly={isReadOnly}
              initialSelected={initialSelectedFields}
              onSelectionChange={onPartialSelectionChange}
            />
          )}

          {template === PlanBenefitOverrideRuleTemplateKey.UPSERT_RULE && (
            <Stack space={20}>
              <div>
                <p className={styles.fieldGroupLabel}>Coverage settings</p>
                <div className={styles.upsertToggleRow}>
                  <Controller
                    name="params.serviceNotCovered"
                    control={control}
                    render={({ field }) => (
                      <label className={styles.toggleItem}>
                        <Switch
                          checked={Boolean(field.value)}
                          onChange={(checked) => field.onChange(checked)}
                          disabled={isReadOnly}
                          size="small"
                        />
                        <span>Service not covered</span>
                      </label>
                    )}
                  />
                  <Controller
                    name="params.appliesToDeductible"
                    control={control}
                    render={({ field }) => (
                      <label className={styles.toggleItem}>
                        <Switch
                          checked={Boolean(field.value)}
                          onChange={(checked) => field.onChange(checked)}
                          disabled={isReadOnly}
                          size="small"
                        />
                        <span>Applies to deductible</span>
                      </label>
                    )}
                  />
                  <Controller
                    name="params.isGrouped"
                    control={control}
                    render={({ field }) => (
                      <label className={styles.toggleItem}>
                        <Switch
                          checked={Boolean(field.value)}
                          onChange={(checked) => field.onChange(checked)}
                          disabled={isReadOnly}
                          size="small"
                        />
                        <span>Grouped benefit</span>
                      </label>
                    )}
                  />
                </div>
              </div>

              {/* ── Cost sharing & Coverage limits ── */}
              <div>
                <p className={styles.fieldGroupLabel}>
                  Cost sharing &amp; Coverage limits
                  <span className={styles.fieldGroupHint}>
                    Use <strong>-1</strong> for values that do not apply
                  </span>
                </p>
                <div className={styles.upsertNumberGrid}>
                  <RHFInput
                    name="params.coinsurance"
                    control={control}
                    label="Coinsurance"
                    placeholder="-1"
                    type="number"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                  <RHFCurrencyInput
                    name="params.copayAmount"
                    control={control}
                    label="Copay ($)"
                    placeholder="-1"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                  <RHFInput
                    name="params.copayDayLimit"
                    control={control}
                    label="Copay day limit"
                    placeholder="-1"
                    type="number"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                  <RHFInput
                    name="params.coinsuranceCopayOrder"
                    control={control}
                    label="Coinsurance/copay order"
                    placeholder="-1"
                    type="number"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                  <RHFCurrencyInput
                    name="params.memberServicePaidCap"
                    control={control}
                    label="Member service cap ($)"
                    placeholder="-1"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                  <RHFInput
                    name="params.coverageVisitLimit"
                    control={control}
                    label="Visit limit"
                    placeholder="-1"
                    type="number"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                  <RHFInput
                    name="params.firstDollarVisits"
                    control={control}
                    label="First-dollar visits"
                    placeholder="-1"
                    type="number"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                  <RHFCurrencyInput
                    name="params.copayAfterFirstVisits"
                    control={control}
                    label="Copay after first visits ($)"
                    placeholder="-1"
                    min={-1}
                    required
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              {/* ── Metadata ── */}
              <div>
                <p className={styles.fieldGroupLabel}>Metadata</p>
                <Stack space={12}>
                  <div className={styles.detailsInlineRow}>
                    <RHFInput
                      name="params.clusterId"
                      control={control}
                      label="Cluster ID"
                      placeholder=""
                      disabled={isReadOnly}
                    />
                    <RHFInput
                      name="params.benefitDisplayValue"
                      control={control}
                      label="Display value"
                      placeholder=""
                      disabled={isReadOnly}
                    />
                  </div>
                  <RHFInput
                    name="params.notes"
                    control={control}
                    label="Benefit notes"
                    placeholder=""
                    disabled={isReadOnly}
                  />
                </Stack>
              </div>
            </Stack>
          )}
        </div>
      </div>
    </div>
  )
}
